import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Modal from '../../../../../components/Modal';
import styles from './UsageModal.module.scss';
import CloseButton from '../../../../../components/CloseButton/CloseButton';
import { CustomButton } from '../../../../../components/Button/Button';

/**
 *
 * @param {number} purchasedMinutes
 * @returns {boolean}
 */
const purchasedMinutesValidator = purchasedMinutes => purchasedMinutes > 0;

/**
 * @param {Date} startDate
 * @param {Date} endDate
 */
const datesValidator = (startDate, endDate) => startDate < endDate;

/**
 * @param {CompanyUsage} usage
 * @returns {UsagePayload}
 */
const usageAsPayload = usage => {
  /** @type {UsagePayload} */ const payload = {};
  payload.startDate = usage.startDate;
  payload.endDate = usage.endDate;
  payload.purchasedMinutes = usage.purchasedMinutes;
  return payload;
};

/**
 * @param {?CompanyUsage} usage
 * @param {boolean} isOpen
 * @param {boolean} isLoading
 * @param {() => void} onCloseClicked
 * @param {(UsagePayload) => void} onConfirmClick
 * @returns {JSX.Element}
 * @constructor
 */
const UsageModal = ({ usage, isOpen, isLoading, onCloseClick, onConfirmClick }) => {
  const [payload, setPayload] = useState(usage ? usageAsPayload(usage) : /** @type {UsagePayload} */ {});
  const { t } = useTranslation();
  /** @type {boolean} */
  const isFormValid = useMemo(() => {
    const isValidCreateContract = !!(payload.startDate && payload.endDate && payload.purchasedMinutes);
    const isValidEditContract = !!(usage && Object.keys(payload).length > 0);
    if (isValidCreateContract || isValidEditContract) {
      const startDate = new Date(payload.startDate ?? usage.startDate);
      const endDate = new Date(payload.endDate ?? usage.endDate);
      const purchasedMinutes = payload.purchasedMinutes ?? usage.purchasedMinutes;
      return datesValidator(startDate, endDate) && purchasedMinutesValidator(purchasedMinutes);
    } else {
      return false;
    }
  }, [usage, payload]);
  const onCloseButtonClick = useCallback(() => {
    setPayload({});
    onCloseClick();
  }, [onCloseClick]);
  const onButtonClick = useCallback(() => {
    onConfirmClick(payload);
  }, [onConfirmClick, payload]);
  const usageStartDate = usage?.startDate;
  const handleStartDateChangeEvent = useCallback(
    e => {
      const date = e.target.value;
      if (date === usageStartDate) {
        const { startDate, ...newPayload } = payload;
        setPayload(newPayload);
      } else {
        setPayload({
          ...payload,
          startDate: date,
        });
      }
    },
    [payload, usageStartDate]
  );
  const usageEndDate = usage?.endDate;
  const handleEndDateChangeEvent = useCallback(
    e => {
      const date = e.target.value;
      if (date === usageEndDate) {
        const { endDate, ...newPayload } = payload;
        setPayload(newPayload);
      } else {
        setPayload({
          ...payload,
          endDate: date,
        });
      }
    },
    [payload, usageEndDate]
  );
  const usagePurchasedMinutes = usage?.purchasedMinutes;
  const handlePurchasedMinutesChangeEvent = useCallback(
    e => {
      const minutes = Number(e.target.value);
      if (minutes === usagePurchasedMinutes) {
        const { purchasedMinutes, ...newPayload } = payload;
        setPayload(newPayload);
      } else {
        setPayload({
          ...payload,
          purchasedMinutes: minutes,
        });
      }
    },
    [payload, usagePurchasedMinutes]
  );

  return (
    <Modal className={styles.usageModal} open={isOpen}>
      <div className={styles.usageModalContainer}>
        <div className={styles.usageHeaderModal}>
          <span className={styles.usageModalTitle}>{usage ? t('company.settings.usage.edit') : t('company.settings.usage.add')}</span>
          <CloseButton type="button" onClose={onCloseButtonClick} />
        </div>
        <div className={styles.usageModalContent}>
          <div className={styles.usageInputContainer}>
            <span className={styles.usageInputHeader}>{t('company.settings.usage.start_date')}</span>
            <input
              type="date"
              name="startDate"
              className={cx(styles.usageInfoInput)}
              defaultValue={usage?.startDate}
              placeholder={t('company.settings.usage.start_date')}
              onChange={handleStartDateChangeEvent}
            />
          </div>
          <div className={styles.usageInputContainer}>
            <span className={styles.usageInputHeader}>{t('company.settings.usage.end_date')}</span>
            <input
              type="date"
              name="endDate"
              className={cx(styles.usageInfoInput)}
              defaultValue={usage?.endDate}
              placeholder={t('company.settings.usage.end_date')}
              onChange={handleEndDateChangeEvent}
            />
          </div>
          <div className={styles.usageInputContainer}>
            <span className={styles.usageInputHeader}>{t('company.settings.usage.purchased_minutes')}</span>
            <input
              type="number"
              name="purchasedMinutes"
              min={1}
              className={cx(styles.usageInfoInput)}
              defaultValue={usage?.purchasedMinutes}
              placeholder={t('company.settings.usage.purchased_minutes')}
              onChange={handlePurchasedMinutesChangeEvent}
            />
          </div>
        </div>
      </div>
      <div className={styles.usageModalFooter}>
        <CustomButton
          className={styles.usageSaveButton}
          label={usage ? t('company.settings.usage.edit') : t('company.settings.usage.add')}
          variant="primary"
          loading={isLoading}
          onClick={onButtonClick}
          disabled={!isFormValid}
          loadingSpinner
        />
      </div>
    </Modal>
  );
};

export default UsageModal;
